// frontend/src/hooks/useVisitorTracker.js
// ✅ النسخة المحسّنة - HTTPS فقط + تقليل الطلبات

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { v4 as uuidv4 } from 'uuid';
import { logger } from '../utils/logger';

const SESSION_ID_KEY = 'visitor_session_id';
const HEARTBEAT_INTERVAL = 60000; // دقيقة واحدة
const REQUEST_TIMEOUT = 5000;
const MAX_RETRIES = 1;

const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  if (ua.includes('Edge')) return 'Edge';
  if (ua.includes('Opera')) return 'Opera';
  return 'Unknown';
};

const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  if (/mobile/i.test(ua)) return 'Mobile';
  if (/tablet|ipad/i.test(ua)) return 'Tablet';
  return 'Desktop';
};

const getOrCreateSessionId = () => {
  let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = uuidv4();
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
  }
  return sessionId;
};

const retryRequest = async (fn, maxRetries = MAX_RETRIES) => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      if (i === maxRetries - 1) throw error;
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
  }
};

export const useVisitorTracker = ({
  clientId = null,
  clientName = null,
  enabled = true
} = {}) => {
  const location = useLocation();
  const sessionIdRef = useRef(getOrCreateSessionId());
  const heartbeatIntervalRef = useRef(null);
  const locationDataRef = useRef(null);
  const isTrackingRef = useRef(false);
  const [isReady, setIsReady] = useState(false);

  // ✅ جلب الموقع مرة واحدة فقط - HTTPS فقط
  useEffect(() => {
    const fetchLocationData = async () => {
      // ✅ تحقق من cache أولاً - لا تطلب مرتين
      const cached = sessionStorage.getItem('visitor_location');
      if (cached) {
        locationDataRef.current = JSON.parse(cached);
        setIsReady(true);
        return;
      }

      // ✅ Cloudflare مباشرة - مجاني بلا حدود
      try {
        const response = await fetch('https://www.cloudflare.com/cdn-cgi/trace', {
          method: 'GET',
          signal: AbortSignal.timeout(5000)
        });

        const data = await response.text();
        const lines = data.split('\n');
        const parseField = (fieldName) => {
          const line = lines.find(l => l.startsWith(`${fieldName}=`));
          return line ? line.split('=')[1] : null;
        };

        locationDataRef.current = {
          country: parseField('loc') || 'Unknown',
          city: 'Unknown'
        };

        // ✅ احفظ في sessionStorage لتجنب طلبات متكررة
        sessionStorage.setItem(
          'visitor_location',
          JSON.stringify(locationDataRef.current)
        );

        logger.debug('✅ Location via Cloudflare:', locationDataRef.current);
        setIsReady(true);

      } catch (error) {
        logger.warn('⚠️ Geolocation failed, using defaults');
        locationDataRef.current = { country: 'Unknown', city: 'Unknown' };
        sessionStorage.setItem(
          'visitor_location',
          JSON.stringify(locationDataRef.current)
        );
        setIsReady(true);
      }
    };

    if (enabled) {
      fetchLocationData();
    } else {
      setIsReady(false);
    }
  }, [enabled]);

  // ✅ تتبع تغيير الصفحات
  useEffect(() => {
    if (!enabled || !isReady) return;

    const trackPageVisit = async () => {
      const visitorType = clientId ? 'client' : 'guest';

      const trackingData = {
        session_id: sessionIdRef.current,
        page_path: location.pathname,
        visitor_type: visitorType,

        country: locationDataRef.current?.country,
        city: locationDataRef.current?.city,
        browser: getBrowserInfo(),
        device: getDeviceInfo()
      };

      logger.debug('📊 Tracking page visit:', {
        page: location.pathname,
        visitorType,
        hasLocation: !!locationDataRef.current
      });

      try {
        await retryRequest(
          () => axios.post(
            `${config.API_BASE_URL}/api/v1/presence/track`,
            trackingData,
            {
              timeout: REQUEST_TIMEOUT,
              headers: {
                'Content-Type': 'application/json',
                ...(clientId && { 'X-Client-ID': clientId })
              }
            }
          )
        );

        logger.debug('✅ Page visit tracked');
        isTrackingRef.current = true;

      } catch (error) {
        logger.warn('⚠️ Page tracking failed (non-critical)');
        isTrackingRef.current = false;
      }
    };

    trackPageVisit();
  }, [location.pathname, clientId, clientName, enabled, isReady]);

  // ✅ Heartbeat
  useEffect(() => {
    if (!enabled || !isReady) return;

    const sendHeartbeat = async () => {
      try {
        await axios.post(
          `${config.API_BASE_URL}/api/v1/presence/heartbeat`,
          { session_id: sessionIdRef.current },
          {
            timeout: 3000,
            headers: { 'Content-Type': 'application/json' }
          }
        );
        logger.debug('💓 Heartbeat sent');
      } catch (error) {
        // heartbeat فشل ليس مشكلة كبيرة
      }
    };

    const initialTimeout = setTimeout(() => {
      sendHeartbeat();
      heartbeatIntervalRef.current = setInterval(
        sendHeartbeat,
        HEARTBEAT_INTERVAL
      );
    }, 10000);

    return () => {
      clearTimeout(initialTimeout);
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [enabled, isReady]);

  // ✅ معالجة المغادرة (بدون sendBeacon لتجنب CORS)
  useEffect(() => {
    if (!enabled || !isReady) return;

    const handleBeforeUnload = () => {
      if (!isTrackingRef.current) return;

      try {
        // ✅ استخدام fetch synchronous بدلاً من sendBeacon
        const data = JSON.stringify({
          session_id: sessionIdRef.current,
          is_widget: false
        });

        // ✅ Fetch مع keepalive (يعمل مثل sendBeacon لكن بدون مشاكل CORS)
        fetch(`${config.API_BASE_URL}/api/v1/presence/leave`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: data,
          keepalive: true  // ✅ مهم: يضمن إرسال الطلب حتى بعد إغلاق الصفحة
        }).catch(() => {
          // تجاهل الأخطاء - الصفحة تُغلق
        });
      } catch (error) {
        // تجاهل الأخطاء عند الإغلاق
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      handleBeforeUnload();
    };
  }, [enabled, isReady]);

  return {
    sessionId: sessionIdRef.current,
    isTracking: isTrackingRef.current
  };
};